import { AsyncValidatorFn, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { DynamicField } from "./dynamic-field.model";
import { DynamicAction } from "./dynamic-action.model";
import { DynamicValidation } from "./dynamic-validation";
import { DynamicRow } from "./dynamic-row.model";
import { DynamicSelect } from "./dynamic-select.model";
import { Option } from '../../models/option.model';
import { Observable, Subject } from "rxjs";
import { DynamicFieldType } from "src/app/enumerators/dynamic-field.enum";
import { DynamicTemplate } from "./dynamic-tamplate.model";
import { DynamicRowTemplate } from "./dynamic-row-template.model";
import { DynamicCol } from "./dynamic-col.model";
import { DynamicRowItem } from "./dynamic-row-item.model";
import { VisibleOn } from "../visible-on.model";
import { DynamicInput } from "./dynamic-input.model";
import { DynamicChips } from "./dynamic-chips.model";
import { DynamicCheckbox } from "./dynamic-checkbox.model";
import { DynamicColor } from "./dynamic-color.model";
import { DynamicGrid } from "./dynamic-grid.model";
import { DynamicDate } from "./dynamic-date.model";
import { DynamicFile } from "./dynamic-file.model";
import { DynamicTextarea } from "./dynamic-textarea.model";
import { DynamicTabView } from "./dynamic-tab-view.model";
import { DynamicTabPanel } from "./dynamic-tab-panel.model";
import { SimpleValidation } from "../simple-validation.model";
import { CustomValidation } from "../custom-validation.model";
import { Control } from "../control.model";
import * as _ from "lodash";
import { DynamicPanel } from "./dynamic-panel.model";
import { DynamicColTemplate } from "./dynamic-col-template.model";
import { DynamicColItem } from "./dynamic-col-item.model";
import { DynamicFieldTemplate } from "./dynamic-field-template.model";

export class DynamicForm<T> {
    data!: T;
    fields?: Array<DynamicField<T>>;
    formGroup!: FormGroup;
    actions?: Array<DynamicAction>;
    validators?: Array<DynamicValidation>;
    errorMessages!: Map<string, string>;
    template?: DynamicTemplate<T>;
    rows?: Array<DynamicRow<T>>;
    dataObserver?: Observable<T>;

    protected dataSubject?: Subject<T>;


    /* The above code is defining a function called `toDynamicSelect` that takes a parameter `field` of
    type `DynamicField<Z>`. It then casts `field` to type `DynamicSelect<U, Z>` and returns it. */
    toDynamicSelect = <U, Z>(field: DynamicField<Z>) => field as DynamicSelect<U, Z>;
    toDynamicFieldTemplate = <Z>(field: DynamicField<Z>) => field as DynamicFieldTemplate<Z>;

    /* The above code is defining a function called `toDynamicRowItem` that takes a parameter `row` of
    type `DynamicRow<T>`. It then casts `row` to type `DynamicRowItem<T>` and returns it. */
    toDynamicRowItem = (row: DynamicRow<T>) => row as DynamicRowItem<T>;

    constructor(source: Partial<DynamicForm<T>>) {
        this.fields = [];
        this.formGroup = new FormGroup({});
        Object.assign(this, source);

        this.dataObserver?.subscribe(data => {
            this.data = data;
        });
    }

    isValid(): boolean {
        if (!this.formGroup?.valid) {
            this.formGroup?.markAllAsTouched();
        } else {
            this.markAsUntouched(this.formGroup);
        }

        return this.formGroup?.valid!;
    }

    getFormData(): T {
        return this.data;
    }

    getFormValue(): T {
        return this.formGroup?.value
    }

    configureTemplate(data: T, rows: Array<DynamicRow<T>>) {
        this.template = new DynamicTemplate<T>({});
        this.template.rows = rows;
        this.template.data = data;
        this.setTemplate(this.template);
        this.configureData(data);
    }

    configureData(data: T) {

        this.data = data;
        this.dataSubject = new Subject<T>();

        this.dataObserver = this.dataSubject?.asObservable();
        this.dataObserver?.subscribe(data => {
            this.data = data;
        });
    }

    setCurrentData(data: T, useTemplate: boolean = false) {
        if (useTemplate && this.template) {
            this.template.data = data;
            this.setTemplate(this.template);
        }

        this.dataSubject?.next(data);
    }



    /**
     * The function sets the grid with the given rows and generates a form template and form group
     * based on the rows.
     * @param rows - An array of DynamicRow objects, where each DynamicRow object represents a row in
     * the grid.
     */
    setGrid(rows: Array<DynamicRow<T>>) {
        try {
            this.rows = rows;
            let controls: Array<Control> = [];
            let index = 0;
            this.generateFormTemplate(rows, controls, index);
            this.formGroup = new FormGroup({});
            this.generateForm(controls, this.formGroup);
            this.configureForm(rows);
        } catch (error) {
            // console.log(error);
        }
    }

    /**
     * The function `generateForm` takes an array of controls and a form group, and recursively
     * generates a form structure based on the control types.
     * @param controls - An array of Control objects. Each Control object represents a form control and
     * contains properties such as type, name, and controls (if it is a form group or array group).
     * @param {FormGroup} formGroup - The `formGroup` parameter is an instance of the `FormGroup` class
     * from Angular's Reactive Forms module. It represents a group of form controls and their values.
     */
    generateForm(controls: Array<Control>, formGroup: FormGroup) {

        controls.forEach(control => {
            switch (control.type) {
                case "arrayGroup": {
                    let innerArrayForm = new FormArray(Array<any>());
                    control.controls?.forEach(formControl => {

                        let innerFormGroup = new FormGroup({});

                        this.generateForm(formControl.controls!, innerFormGroup);

                        innerArrayForm.push(innerFormGroup);;
                    });

                    formGroup.addControl(control.name!, innerArrayForm);

                    break;
                }
                case "formGroup": {
                    let innerFormGroup = new FormGroup({});

                    this.generateForm(control.controls!, innerFormGroup);

                    formGroup.addControl(control.name!, innerFormGroup);
                    break;
                }
                case "generic":
                    formGroup.addControl(control.name!, control.field);
                    break;
            }
        })

    }

    /**
     * The function `configureForm` iterates through an array of dynamic rows and configures form
     * controls based on their visibility and validation conditions.
     * @param rows - An array of DynamicRow objects. Each DynamicRow object represents a row in a form
     * and contains information about the fields and columns in that row.
     */
    configureForm(rows: Array<DynamicRow<T>>) {
        rows!.forEach(row => {


            if (row.visibleOn) {

                if (row.visibleOn.isCustomValidation) {

                    row.visibleOn.customValidation?.validations?.forEach(item => {
                        let field = this.findField('alias', item.controlAlias, this.rows!);
                        field.formControl?.valueChanges.subscribe(value => {
                            item.validate(value);
                        });
                    });

                } else {
                    let field = this.findField('alias', row.visibleOn.simpleValidation?.controlAlias, this.rows!);
                    field.formControl?.valueChanges.subscribe(value => {
                        row.visibleOn?.simpleValidation?.validate(value);
                    });
                }


            }
            row.columns?.forEach(col => {

                if (col.field?.type == DynamicFieldType.Select) {
                    if (this.toDynamicSelect(col.field).filterOn) {
                        let field = this.findField('alias', this.toDynamicSelect(col.field).filterOn?.controlAlias, this.rows!);
                        field.formControl?.valueChanges.subscribe(value => {
                            this.toDynamicSelect(col.field!).filterOn?.filterSubject.next(value);
                        });
                    }
                }

                if (col.visibleOn) {
                    if (col.visibleOn.isCustomValidation) {

                        col.visibleOn.customValidation?.validations?.forEach(item => {
                            let field = this.findField('alias', item.controlAlias, this.rows!);
                            field.formControl?.valueChanges.subscribe(value => {
                                item.validate(value);
                            });
                        });

                    } else {
                        let field = this.findField('alias', col.visibleOn.simpleValidation?.controlAlias, this.rows!);
                        field.formControl?.valueChanges.subscribe(value => {
                            col.visibleOn?.simpleValidation?.validate(value);
                        });
                    }
                }

                if (col.rows) {
                    this.configureForm(col.rows);
                }
            });

            if (row.type == "tabview") {
                (row as DynamicTabView<T>).tabs?.forEach(tab => {
                    if (tab.rows) {
                        this.configureForm(tab.rows);
                    }
                });
            }
        });
    }

    getArrarControl(name: string): Control {
        let arrayControl = new Control({
            controls: [],
            name: name,
            type: "arrayGroup"
        });

        return arrayControl;
    }

    /**
     * The function generates a form template based on an array of dynamic rows and controls, with
     * support for nested rows and arrays.
     * @param rows - An array of DynamicRow objects. Each DynamicRow object represents a row in the
     * form template.
     * @param controls - An array of Control objects that represent the form controls in the form
     * template.
     * @param {number} index - The index parameter is used to keep track of the position of each
     * control in the form template. It is incremented each time a control is added to the template.
     * @param {boolean} [isItem=false] - A boolean flag indicating whether the current row is an item
     * in an array.
     * @param {boolean} [isArray=false] - A boolean value indicating whether the current control is an
     * array or not. If true, it means the control is an array and will have multiple values.
     */
    generateFormTemplate(rows: Array<DynamicRow<T>>, controls: Array<Control>, index: number, isItem: boolean = false, isArray: boolean = false) {
        let arrayIndex: number = 0

        rows.forEach(row => {
            if (row.formProperty) {
                if (row.isArray) {
                    let arrayControl = this.getArrarControl(row.formProperty);
                    let existingArrayGroup = this.findArrayGroup(row.formProperty, controls);
                    let existArrayGroup = existingArrayGroup ?? false;
                    arrayControl = existingArrayGroup || arrayControl;
                    if (row.columns) {
                        arrayIndex = 0;
                        row.columns.forEach(col => {
                            arrayControl.controls?.push(new Control({
                                controls: [],
                                name: arrayIndex + '',
                                type: "formGroup"
                            }));
                            arrayIndex++;
                        });

                        arrayIndex = 0;
                        row.columns.forEach(col => {
                            this.generateColumnTemplate(col, arrayControl.controls!, index, true, true, arrayIndex);
                            arrayIndex++;
                        });
                    }

                    if (!existArrayGroup) {
                        controls.push(arrayControl);
                    }
                }

            }

            row.columns?.forEach(col => {
                if (col.formProperty) {

                    if (col.isArray) {

                        let arrayControl = this.getArrarControl(col.formProperty);

                        let existingArrayGroup = this.findArrayGroup(col.formProperty!, controls);
                        let existArrayGroup = existingArrayGroup ?? false;
                        arrayControl = existingArrayGroup ? existingArrayGroup : arrayControl;

                        if (col.rows) {
                            let rowArrayIndex = 0;

                            if (!existArrayGroup) {
                                col.rows.forEach(row => {
                                    arrayControl.controls?.push(new Control({
                                        controls: [],
                                        name: rowArrayIndex + '',
                                        type: "formGroup"
                                    }));
                                });
                            }


                            this.generateFormTemplate(col.rows, arrayControl.controls!, index, true, true);
                            rowArrayIndex++;
                        }

                        if (!existArrayGroup) {
                            controls.push(arrayControl);
                        }

                    } else {

                        let formControl = new Control({
                            controls: [],
                            name: col.formProperty!,
                            type: "formGroup"
                        });

                        if (col.rows) {
                            this.generateFormTemplate(col.rows, formControl.controls!, index, true, false);
                        }
                        controls.push(formControl);
                    }

                    index++
                } else {

                    this.generateColumnTemplate(col, controls, index, isItem, isArray, arrayIndex);

                }
            });

            if (row.type == "tabview") {
                (row as DynamicTabView<T>).tabs?.forEach(tab => {
                    if (tab.rows) {
                        this.generateFormTemplate(tab.rows, controls, index, isArray);
                    }
                });
            }

            arrayIndex++;
        });

    }


    generateColumnTemplate(col: DynamicCol<T>, controls: Array<Control>, index: number, isItem: boolean = false, isArray: boolean = false, arrayIndex: number = 0) {
        if (col.field && ![DynamicFieldType.Html, DynamicFieldType.Icon, DynamicFieldType.DivText, DynamicFieldType.Template].includes(col.field.type)) {

            let validators: (ValidatorFn)[] | undefined = col.field.validators?.map(item => item.validator);
            let asyncValidators: (AsyncValidatorFn)[] | undefined = col.field.validators?.map(item => item.asyncValidator);

            col.field.errorMessages = new Map<string, string>;

            col.field.validators?.forEach(item => {
                if (item.isCustom) {
                    col.field?.errorMessages.set(item.customType!, item.errorMessage);
                } else {
                    col.field?.errorMessages.set(item.type!, item.errorMessage);
                }
            });

            try {
                col.field.formControl = new FormControl(col.field.value, validators ? Validators.compose(validators) : undefined, asyncValidators ? Validators.composeAsync(asyncValidators) : undefined);
            } catch (error) {
                // console.log(error);
            }

            if (col.field.disabled) {
                col.field.formControl?.disable();
            }

            if (col.field.onChange) {
                col.field.formControl?.valueChanges.subscribe(value => {
                    col.field?.onChange?.(value);
                });
            }

            let fieldControl = new Control({
                field: col.field.formControl,
                name: col.field.property.toString(),
                type: "generic"
            });

            if (isItem) {
                if (isArray) {
                    controls[arrayIndex].controls?.push(fieldControl);
                } else {
                    controls.push(fieldControl);
                }
            } else {
                controls.push(fieldControl);
            }

            col.field.formControlIndex = index;
            this.fields?.push(col.field);

            index++
        }
        if (col.rows) {
            this.generateFormTemplate(col.rows, controls, index, isItem, isArray);
        }
    }


    findArrayGroup(name: string, controls: Array<Control>): Control | undefined {
        try {
            for (let control of controls) {
                if (control.type == "arrayGroup" && control.name == name) {
                    return control;
                }

                if (control.controls) {
                    let foundControl = this.findArrayGroup(name, control.controls);
                    if (foundControl) {
                        return foundControl;
                    }
                }
            }
        } catch (error) {
            // console.log(error);
        }

        return undefined;
    }

    /**
     * The function sets validations for a specific field in a form group, including both synchronous
     * and asynchronous validators, and updates the field's error messages.
     * @param {string} fieldProperty - The fieldProperty parameter is a string that represents the
     * property of the field that needs to be validated.
     */
    setFieldValidations(fieldProperty: string) {

        let field = this.findField('property', fieldProperty, this.rows!);
        let validators: (ValidatorFn)[] | undefined = field.validators?.map(item => item.validator);
        let asyncValidators: (AsyncValidatorFn)[] | undefined = field.validators?.map(item => item.asyncValidator);

        if (validators) {
            this.formGroup.controls[fieldProperty].setValidators(validators);
        }

        if (asyncValidators && !asyncValidators.some(item => item === undefined)) {
            this.formGroup.controls[fieldProperty].setAsyncValidators(asyncValidators);
        }


        field.errorMessages = new Map<string, string>;

        field.validators?.forEach(item => {
            if (item.isCustom) {
                field.errorMessages.set(item.customType!, item.errorMessage);
            } else {
                field.errorMessages.set(item.type!, item.errorMessage);
            }
        });

        this.formGroup.controls[fieldProperty].updateValueAndValidity();

    }

    /**
     * The function `findField` recursively searches for a field in an array of rows based on a given
     * property and property value.
     * @param {string} property - A string representing the property name to search for in the fields.
     * @param {any} propertyValue - The value of the property you are searching for in the fields.
     * @param rows - An array of DynamicRow objects. Each DynamicRow object represents a row in a table
     * or form.
     * @returns a DynamicField<T> object.
     */
    private findField(property: string, propertyValue: any, rows: Array<DynamicRow<T>>): DynamicField<T> {
        let field: DynamicField<T>;
        let index = 0;
        let fieldFinded: boolean = false;
        try {
            do {
                for (let col of rows[index].columns!) {
                    if (col.field && col.field[property as keyof DynamicField<T>]?.toString() === propertyValue) {
                        field = col.field!;
                        fieldFinded = true;
                        return field;
                    } else {
                        field = this.findField(property, propertyValue, col.rows!);
                        if (field) {
                            fieldFinded = true;
                            return field;
                        }
                    }
                }

                if (rows[index].type == "tabview") {
                    (rows[index] as DynamicTabView<T>).tabs?.forEach(tab => {
                        if (tab.rows) {
                            this.findField(property, propertyValue, tab.rows);
                        }
                    });
                }
                index++;
            } while (!fieldFinded && index < rows.length);
        } catch (error) {
            // console.log(error);
        }

        return field!;
    }


    /**
     * The function creates an array of options based on a dynamic select field.
     * @param field - A DynamicSelect object with two properties:
     * @returns The function `createOptions` returns an array of `Option` objects.
     */
    createOptions<U>(field: DynamicSelect<U, T>): Array<Option> {
        let options: Array<Option> | undefined = [];

        options = field.source?.map(item => {
            return new Option({
                id: this.isNumeric(item[field.nameValue] as string) ? parseInt(item[field.nameValue] as string) : item[field.nameValue] as string,
                name: item[field.displayValue] as string
            });
        });

        return options!;
    }

    private isNumeric = (val: string): boolean => {
        return !isNaN(Number(val));
    }

    markAsTouched(form: FormGroup) {
        for (var index in form.controls) {
            form.controls[index].markAsTouched();
            this.markAsTouched(form.controls[index] as FormGroup);
        }
    }

    markAsUntouched(form: FormGroup) {
        for (var index in form.controls) {
            form.controls[index].markAsUntouched();
            form.controls[index].updateValueAndValidity();
            form.controls[index].markAsPristine();
            this.markAsUntouched(form.controls[index] as FormGroup);
        }
    }

    removeControls(form: FormGroup) {
        while (Object.keys(form.controls).length) {
            const toRemove = Object.keys(form.controls)[0];
            form.removeControl(toRemove)
        }
    }

    /**
     * The function sets a template and generates a grid based on the template's rows and data.
     * @param template - The template parameter is of type DynamicTemplate<T>.
     */
    setTemplate(template: DynamicTemplate<T>) {
        this.template = template;

        let grid = this.generateGrid(this.template.rows!, template.data);

        this.setGrid(grid);
    }

    /**
     * The function generates a grid based on a given set of rows and data.
     * @param rows - An array of DynamicRow objects. Each DynamicRow object represents a row in the
     * grid and contains information about how to generate the row.
     * @param {U} data - The `data` parameter is an object of type `U` which contains the data to be
     * used for generating the grid.
     * @returns The function `generateGrid` returns a new grid, which is an array of `DynamicRow<U>`
     * objects.
     */
    generateGrid<U>(rows: Array<DynamicRow<U>>, data: U) {

        let newGrid: Array<DynamicRow<U>> = [];

        rows.forEach(row => {
            if (row instanceof DynamicRowTemplate) {
                if (row.isArray) {
                    let items = (data[row.property as keyof U] as Array<U>).length;
                    for (let index = 0; index < items; index++) {
                        newGrid.push(this.generateRow(row, (data[row.property as keyof U] as Array<U>)[index], index));
                    }

                } else {
                    newGrid.push(this.generateRow(row, data[row.property as keyof U] as U, 0));
                }
            } else {
                newGrid.push(this.generateRow(row, data, 0));
            }
        });

        return newGrid;
    }

    /**
     * The function `generateRow` takes in a dynamic row object, data, and an index, and generates a
     * new dynamic row object based on the type of the original row.
     * @param row - The `row` parameter is an object that represents a dynamic row in a form. It
     * contains information about the type of row, the columns within the row, and any visibility
     * conditions for the row.
     * @param {U} data - The `data` parameter is the object that contains the data for the row. It is
     * of type `U`, which is a generic type that represents the type of the data object.
     * @param {number} index - The index parameter is the index of the row in the list of rows. It is
     * used to identify the position of the row in the list.
     * @returns The function `generateRow` returns a new instance of `DynamicRow<U>` or its subclasses
     * (`DynamicRowItem<U>` or `DynamicTabView<U>`).
     */
    generateRow<U>(row: DynamicRow<U>, data: U, index: number) {
        let newRow = new DynamicRow<U>({
            columns: [],
            visibleObserver: row.visibleObserver,
            formProperty: row.formProperty,
            isArray: row.isArray
        });

        switch (row.type) {
            case "nested-columns":
                newRow = new DynamicRow<U>({
                    columns: [],
                    visibleObserver: row.visibleObserver,
                    formProperty: row.formProperty,
                    isArray: row.isArray,
                    type: row.type
                });
                break;
            case "template":
                newRow = new DynamicRowItem<U>({
                    columns: [],
                    data: data,
                    formGroupIndex: index,
                    type: (row as DynamicRowTemplate<U>).toType,
                    property: (row as DynamicRowTemplate<U>).property as keyof U,
                    visibleObserver: row.visibleObserver
                });
                break;
            case "tabview":
                newRow = new DynamicTabView<U>({
                    tabs: []
                })
                break
            case 'panel':
                newRow = new DynamicPanel<U>({
                    columns: [],
                    header: (row as DynamicPanel<U>).header,
                    styleClass: (row as DynamicPanel<U>).styleClass,
                    visibleObserver: row.visibleObserver,
                    visibleOn: row.visibleOn,
                    visible: row.visible,
                    type: row.type
                })

                break;
        }

        if (row.visibleOn) {

            let visibleOn = this.getVisibleOn(row.visibleOn, data);

            if (visibleOn.isCustomValidation) {
                newRow.setVisibleObserver(visibleOn.customValidation?.getObservable()!);
            } else {
                newRow.setVisibleObserver(visibleOn.simpleValidation?.getObservable()!);
            }

            newRow.visibleOn = visibleOn;
        }

        row.columns?.forEach(column => {


            if (column instanceof DynamicColTemplate) {

                if (column.isArray) {
                    let items = (data[column.formProperty as keyof U] as Array<U>).length;
                    for (let index = 0; index < items; index++) {
                        newRow.columns?.push(this.generateColumn(column, (data[column.formProperty as keyof U] as Array<U>)[index], index, '', true));
                    }
                } else {
                    newRow.columns?.push(this.generateColumn(column, data, index, '', true));
                }

            } else {

                if (row.type == "switch") {
                    let value = _.get(data, row.switchProperty!);
                    if (value == column.switchCaseValue) {
                        newRow.columns?.push(this.generateColumn(column, data, index));
                    } else if (column.switchCaseValue == 'default') {

                        if (row.switchDefaultValues?.includes(value + '')) {
                            newRow.columns?.push(this.generateColumn(column, data, index));
                        }

                    }
                } else {
                    newRow.columns?.push(this.generateColumn(column, data, index));
                }
            }

        });

        if (row.type == "tabview") {
            (row as DynamicTabView<U>).tabs?.forEach(tab => {
                (newRow as DynamicTabView<U>).tabs?.push(this.generateTabPanel(tab, data, index));
            });

        }

        return newRow;
    }

    /**
     * The function generates a new tab panel based on the provided tab panel object and data.
     * @param tabPanel - The `tabPanel` parameter is an object of type `DynamicTabPanel<U>`. It
     * represents a tab panel component with a header and rows of data.
     * @param {U} data - The `data` parameter is of type `U` and represents the data that will be used
     * to generate the tab panel. It is used as an input to the `generateGrid` function, which
     * generates the grid for the tab panel.
     * @param {number} index - The index parameter is the index of the tab panel in the list of tab
     * panels. It is used to identify the specific tab panel that needs to be generated.
     * @returns a new instance of the DynamicTabPanel class with the same header as the original
     * tabPanel. If the original tabPanel has rows, the function also generates a new grid for those
     * rows using the generateGrid function and assigns it to the newTabPanel's rows property.
     */
    generateTabPanel<U>(tabPanel: DynamicTabPanel<U>, data: U, index: number) {
        let newTabPanel = new DynamicTabPanel<U>({
            header: tabPanel.header
        });

        if (tabPanel.rows && tabPanel.rows.length > 0) {
            newTabPanel.rows = this.generateGrid(tabPanel.rows, data);
        }

        return newTabPanel;
    }

    /**
     * The function `generateColumn` takes in a column object, data, index, and parentProperty, and
     * returns a new column object with updated properties based on the input.
     * @param column - The `column` parameter is of type `DynamicCol<U>`, which is a generic type
     * representing a dynamic column in a grid. It contains various properties such as `field`, `span`,
     * `formProperty`, `isArray`, `classes`, `visibleObserver`, and `visible`.
     * @param {U} data - The `data` parameter is the object that contains the data for the column. It
     * is of type `U`, which is a generic type that can be any type.
     * @param {number} index - The index parameter is the index of the current column being generated.
     * It is used to keep track of the position of the column in the grid.
     * @param {string} [parentProperty] - The `parentProperty` parameter is a string that represents
     * the property of the parent object that the column belongs to. It is used to determine the field
     * property of the new column.
     * @returns a new column object with properties set based on the input parameters and data.
     */
    generateColumn<U>(column: DynamicCol<U>, data: U, index: number, parentProperty: string = '', fromTemplate: boolean = false) {
        let newColumn = column.type == 'template' ? this.createDynamicColItem(column, data, index, fromTemplate) : this.createDynamicCol(column, fromTemplate);

        this.assignFieldValues(newColumn, data);
        this.handleColumnVisibility(newColumn, column, data);
        this.generateRowsIfNeeded(newColumn, column, data);

        return newColumn;
    }

    private createDynamicCol<U>(column: DynamicCol<U>, fromTemplate: boolean): DynamicCol<U> {
        return new DynamicCol({
            field: this.getField(column.field!),
            span: column.span,
            formProperty: fromTemplate ? undefined : column.formProperty,
            isArray: column.isArray,
            classes: column.classes,
            visibleObserver: column.visibleObserver,
            visible: column.visible,
            rows: column.rows
        });
    }

    private createDynamicColItem<U>(column: DynamicCol<U>, data: U, index: number, fromTemplate: boolean): DynamicColItem<U> {
        return new DynamicColItem({
            field: this.getField(column.field!),
            span: column.span,
            type: (column as DynamicColTemplate<U>).toType,
            property: (column as DynamicColTemplate<U>).property as keyof U,
            formProperty: fromTemplate ? undefined : column.formProperty,
            formGroupIndex: index,
            isArray: column.isArray,
            classes: column.classes,
            visibleObserver: column.visibleObserver,
            visible: column.visible,
            data: data,
            rows: column.rows
        });
    }

    private assignFieldValues<U>(newColumn: DynamicCol<U> | DynamicColItem<U>, data: U) {
        if (newColumn.field) {
            newColumn.field.value = data[newColumn.field.property] ? data[newColumn.field.property] : null;
            if (newColumn.field.labelProperty) {
                newColumn.field.label = data[newColumn.field.labelProperty] ? data[newColumn.field.labelProperty] + '' : '';
            }
            if (newColumn.field.type == DynamicFieldType.Select && this.toDynamicSelect(newColumn.field).filterOn) {
                this.toDynamicSelect(newColumn.field!).setFilterObserver(this.toDynamicSelect(newColumn.field!).filterOn?.setFilterObservable()!);
            }
            if (newColumn.field.type == DynamicFieldType.Template) {
                this.toDynamicFieldTemplate(newColumn.field!).data = data;
            }
        }
    }

    private handleColumnVisibility<U>(newColumn: DynamicCol<U> | DynamicColItem<U>, column: DynamicCol<U>, data: U) {
        if (column.visibleOn) {
            let visibleOn = this.getVisibleOn(column.visibleOn, data);
            newColumn.setVisibleObserver(visibleOn.isCustomValidation ? visibleOn.customValidation?.getObservable()! : visibleOn.simpleValidation?.getObservable()!);
            newColumn.visibleOn = visibleOn;
        }
    }

    private generateRowsIfNeeded<U>(newColumn: DynamicCol<U> | DynamicColItem<U>, column: DynamicCol<U>, data: U) {
        if (column.rows && column.rows.length > 0) {
            newColumn.rows = this.generateGrid(column.rows, data);
        }
    }

    /**
     * The function `getVisibleOn` takes a `VisibleOn` template and data, and returns a new `VisibleOn`
     * object with the appropriate properties set based on the template and data.
     * @param {VisibleOn} visibleOnTemplate - The `visibleOnTemplate` parameter is an object that
     * contains information about the visibility conditions for a field or component. It has the
     * following properties:
     * @param {U} data - The `data` parameter is an object of type `U` which represents the data that
     * will be used for validation.
     * @returns an instance of the `VisibleOn` class.
     */
    getVisibleOn<U>(visibleOnTemplate: VisibleOn, data: U) {
        let visibleOn = new VisibleOn({
            isCustomValidation: visibleOnTemplate.isCustomValidation,
            clearFieldValidations: visibleOnTemplate.clearFieldValidations
        });
        if (visibleOnTemplate.isCustomValidation) {

            let validations: Array<SimpleValidation> = [];

            visibleOnTemplate.customValidation?.validations?.forEach(item => {
                let validation = this.getSimpleValidation(item);
                if (validation.valueProperty !== undefined) {
                    validation.value = data[validation.valueProperty as keyof U];
                }

                if (validation.isStatic) {
                    if (validation.staticProperty !== undefined) {
                        validation.staticValue = data[validation.staticProperty as keyof U];
                        validation.validate(0);
                    }
                } else if (validation.onlyValue) {
                    validation.validate(0);
                }

                validations.push(validation);
            });

            visibleOn.customValidation = new CustomValidation({
                pattern: visibleOnTemplate.customValidation?.pattern,
                validations: validations
            });


        } else {
            visibleOn.simpleValidation = this.getSimpleValidation(visibleOnTemplate.simpleValidation!);
            if (visibleOn.simpleValidation.valueProperty !== undefined) {
                visibleOn.simpleValidation.value = data[visibleOnTemplate.simpleValidation?.valueProperty as keyof U];
            }

            if (visibleOn.simpleValidation.isStatic) {
                if (visibleOn.simpleValidation.staticProperty !== undefined) {
                    visibleOn.simpleValidation.staticValue = data[visibleOnTemplate.simpleValidation?.staticProperty as keyof U];
                    visibleOn.simpleValidation.validate(0);
                }
            } else if (visibleOn.simpleValidation.onlyValue) {
                visibleOn.simpleValidation.validate(0);
            }
        }

        return visibleOn;

    }

    /**
     * The function creates a new instance of the SimpleValidation class and returns it.
     * @param {SimpleValidation} validation - The `validation` parameter is an object that contains the
     * following properties:
     * @returns an instance of the SimpleValidation class.
     */
    getSimpleValidation(validation: SimpleValidation) {
        let simpleVaidation = new SimpleValidation({
            controlAlias: validation.controlAlias,
            onlyValue: validation.onlyValue,
            response: validation.response,
            value: validation.value,
            patternIndex: validation.patternIndex,
            valueProperty: validation.valueProperty,
            validationType: validation.validationType,
            isStatic: validation.isStatic,
            staticProperty: validation.staticProperty,
            staticValue: validation.staticValue
        });

        return simpleVaidation;
    }

    /**
     * The getField function returns a dynamic input component based on the type of the field passed as
     * an argument.
     * @param field - The `field` parameter is of type `DynamicField<U>`.
     * @returns an instance of a dynamic field component based on the type of the field passed as an
     * argument. If the field is of type DynamicFieldType.Text, it returns a new instance of
     * DynamicInput. If the field is of type DynamicFieldType.Chip, it returns a new instance of
     * DynamicChips. If the field is of type DynamicFieldType.Boolean, it returns a new instance of
     * Dynamic
     */
    getField<U>(field: DynamicField<U>) {

        if (field) {
            switch (field.type) {
                case DynamicFieldType.Text:
                    return new DynamicInput(field);
                case DynamicFieldType.Chip:
                    return new DynamicChips(field);
                case DynamicFieldType.Boolean:
                    return new DynamicCheckbox(field);
                case DynamicFieldType.Color:
                    return new DynamicColor(field);
                case DynamicFieldType.Grid:
                    return new DynamicGrid(field);
                case DynamicFieldType.Date:
                    return new DynamicDate(field);
                case DynamicFieldType.File:
                    return new DynamicFile(field);
                case DynamicFieldType.Password:
                    return new DynamicInput(field);
                case DynamicFieldType.Select:
                    return new DynamicSelect(field);
                case DynamicFieldType.TextArea:
                    return new DynamicTextarea(field);
                case DynamicFieldType.Template:
                    return new DynamicFieldTemplate(field);
            }
        }


        return field;
    }
}